import React, { useCallback } from 'react'

import { TtTrayImage } from '@23/ui-react'

import {
  useOverlayState,
  removeOverlay,
  setChyron,
} from '../../../../firebase/hooks/useOverlays'
import { useLocalParticipant } from '../../../../webrtc/webRTCRoom'
import usePlaybackIntent from '../../../hooks/usePlaybackIntent'
import { ChyronType } from '../../../../firebase/model/chyron-model'
import {
  switchToTray,
  useIsTrayInFloor,
} from '../../../../firebase/hooks/useFloor'
import {
  setActiveCard,
  useTrayCard,
  useTrayCardState,
} from '../../../../firebase/hooks/useTray'
import { type PropsWithTrayItemChildren } from '../TrayItem'

interface ImageCardProps {
  id: string
}

const ImageCard = ({
  id,
  children,
}: PropsWithTrayItemChildren<ImageCardProps>) => {
  const card = useTrayCard(id)
  const cardState = useTrayCardState(id)
  const overlay = useOverlayState()
  const isTrayInFloor = useIsTrayInFloor()
  const { setPlaybackPauseIntent } = usePlaybackIntent()
  const localParticipant = useLocalParticipant()

  const clearSpeakerChyron = useCallback(() => {
    if (overlay?.type === ChyronType.Speaker) {
      void setChyron(null)
    }
  }, [overlay?.type])

  const onCardClickHandler = useCallback(() => {
    if (card?.type === 'image' && card.thumbnail) {
      setActiveCard(id)
      void setPlaybackPauseIntent()

      if (overlay?.type === ChyronType.Speaker) {
        void removeOverlay()
      }

      if (!isTrayInFloor) {
        void switchToTray()
      }

      clearSpeakerChyron()
    }
  }, [
    card,
    id,
    setPlaybackPauseIntent,
    overlay?.type,
    isTrayInFloor,
    clearSpeakerChyron,
  ])

  if (card?.type !== 'image') return null

  // image is midst being uploaded by other participant
  if (card.participantId && +card.participantId !== localParticipant?.id) {
    return null
  }

  return (
    <TtTrayImage
      src={card.thumbnail}
      label={card.label}
      selected={cardState === 'active'}
      disabled={cardState === 'done'}
      onTtClick={(event) => {
        if (event.target === event.currentTarget) {
          onCardClickHandler()
        }
      }}
    >
      {children?.({ isMedia: true })}
    </TtTrayImage>
  )
}

export default ImageCard
